import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Heart, ChevronLeft, Star, Shield, Users, BookOpen, Stethoscope, HelpCircle, Sparkles, ChevronRight, Accessibility, Lightbulb, MapPin, FileText, Palette, HandHeart, ExternalLink, MessageCircle, Send, Loader2, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4791deb4-db22-4895-9e0a-21aa9f309cff/KiddiplayClub-Logo.jpg";

const CATEGORY_INFO = {
  tips: { label: "Tips & Advice", icon: Lightbulb, color: "amber", emoji: "💡" },
  accessible_outings: { label: "Accessible Outings", icon: MapPin, color: "emerald", emoji: "🎯" },
  govt_updates: { label: "Govt/NHS Updates", icon: FileText, color: "blue", emoji: "📋" },
  sensory_activities: { label: "Sensory Activities", icon: Palette, color: "purple", emoji: "🌈" },
  support_resources: { label: "Support Resources", icon: HandHeart, color: "pink", emoji: "🤝" },
  personal_story: { label: "Kiera's Story", icon: Heart, color: "violet", emoji: "💜" }
};

const SENFocus = () => {
  const [activeSection, setActiveSection] = useState("overview");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory 
        ? `${API}/sen/posts?category=${selectedCategory}&limit=20`
        : `${API}/sen/posts?limit=20`;
      const res = await axios.get(url);
      setPosts(res.data.posts || []);
    } catch (error) {
      console.error("Error fetching SEN posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/sen/posts/${postId}/comments`);
      setComments(prev => ({ ...prev, [postId]: response.data.comments || [] }));
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const toggleComments = async (postId) => {
    const isExpanded = expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
    if (!isExpanded && !comments[postId]) {
      await fetchComments(postId);
    }
  };

  const submitComment = async (postId) => {
    if (!token) {
      toast.error("Please log in to comment!");
      return;
    }
    const content = newComment[postId]?.trim();
    if (!content) {
      toast.error("Please write a comment first!");
      return;
    }
    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      await axios.post(
        `${API}/sen/posts/${postId}/comments`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Comment submitted for moderation! 💜");
      setNewComment(prev => ({ ...prev, [postId]: "" }));
    } catch (error) {
      toast.error("Failed to submit comment");
    } finally {
      setSubmittingComment(prev => ({ ...prev, [postId]: false }));
    }
  };

  const sections = [
    { id: "overview", title: "SEN ZEN", icon: Accessibility, color: "from-teal-500 to-cyan-500" },
    { id: "posts", title: "Kiera's Posts", icon: Star, color: "from-purple-500 to-pink-500" },
    { id: "resources", title: "Resources", icon: BookOpen, color: "from-orange-500 to-amber-500" },
    { id: "nhs-cwdt", title: "NHS & CWDT", icon: Stethoscope, color: "from-blue-500 to-indigo-500" }
  ];

  const quickLinks = [
    { title: "IPSEA - SEN Legal Advice", url: "https://www.ipsea.org.uk", desc: "Free legal advice for SEN families" },
    { title: "Contact a Family", url: "https://contact.org.uk", desc: "Support for disabled children's families" },
    { title: "Autism Alliance UK", url: "https://autism-alliance.org.uk", desc: "Autism support network" },
    { title: "SENDIASS", url: "https://councilfordisabledchildren.org.uk/what-we-do/networks/sendiass", desc: "Special Educational Needs advice" },
    { title: "Scope", url: "https://www.scope.org.uk", desc: "Disability equality charity" },
    { title: "National Autistic Society", url: "https://www.autism.org.uk", desc: "UK's leading autism charity" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-white pb-20">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-teal-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back</span>
            </Link>
            <div className="h-6 w-px bg-slate-200" />
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="KiddiPlay" className="w-8 h-8 rounded-full" />
              <span className="font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>SEN Focus</span>
            </Link>
          </div>
          <Link to="/register">
            <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-full text-sm">
              Join KiddiPlay
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-10 text-center bg-gradient-to-b from-teal-100/50 to-transparent">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Accessibility className="w-4 h-4" />
            SEN Focus
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            SEN ZEN
          </h1>
          <p className="text-lg text-slate-600 mb-2">
            A dedicated space for SEN families. Resources, support, and a community that understands.
          </p>
          <p className="text-sm text-teal-600 font-medium">
            Led by Kiera - sharing experiences, tips, and favourites for SEN families 💜
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <div className="px-6 mb-8">
        <div className="max-w-4xl mx-auto flex flex-wrap gap-2 justify-center">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => { setActiveSection(section.id); setSelectedCategory(null); }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeSection === section.id
                  ? `bg-gradient-to-r ${section.color} text-white shadow-lg`
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              <section.icon className="w-4 h-4" />
              {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="px-6">
        <div className="max-w-5xl mx-auto">
          
          {/* SEN ZEN Overview */}
          {activeSection === "overview" && (
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-teal-100">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Accessibility className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                      Welcome to SEN ZEN
                    </h2>
                    <p className="text-slate-600">
                      A calm, understanding space where SEN families can find resources, connect with others, and get practical guidance.
                    </p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { icon: Heart, title: "Understanding Community", desc: "Connect with families who get it" },
                    { icon: BookOpen, title: "Curated Resources", desc: "Helpful guides and information" },
                    { icon: Shield, title: "Safe Space", desc: "Judgment-free support" },
                    { icon: Sparkles, title: "Practical Tips", desc: "Real advice from real families" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-teal-50 rounded-xl">
                      <item.icon className="w-5 h-5 text-teal-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-slate-800 text-sm">{item.title}</h4>
                        <p className="text-xs text-slate-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Quick Links */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                  <button
                    key={key}
                    onClick={() => { setActiveSection("posts"); setSelectedCategory(key); }}
                    className={`p-4 rounded-xl border-2 border-${info.color}-200 bg-${info.color}-50 hover:bg-${info.color}-100 transition-colors text-left`}
                  >
                    <span className="text-2xl mb-2 block">{info.emoji}</span>
                    <h4 className={`font-semibold text-${info.color}-800 text-sm`}>{info.label}</h4>
                  </button>
                ))}
              </div>

              {/* Latest Posts Preview */}
              {posts.length > 0 && (
                <div>
                  <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-teal-500" />
                    Latest from Kiera
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {posts.slice(0, 4).map(post => {
                      const catInfo = CATEGORY_INFO[post.category] || CATEGORY_INFO.tips;
                      return (
                        <div key={post.post_id} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${catInfo.color}-100 text-${catInfo.color}-700 mb-2`}>
                            {catInfo.emoji} {catInfo.label}
                          </span>
                          <h4 className="font-semibold text-slate-800 mb-1">{post.title}</h4>
                          <p className="text-sm text-slate-600 line-clamp-2">{post.content}</p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-center mt-4">
                    <button
                      onClick={() => setActiveSection("posts")}
                      className="text-teal-600 font-medium text-sm hover:text-teal-700 inline-flex items-center gap-1"
                    >
                      See all posts <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Kiera's Posts */}
          {activeSection === "posts" && (
            <div className="space-y-6">
              {/* Category Filters */}
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    !selectedCategory ? "bg-teal-500 text-white" : "bg-white text-slate-600 border border-slate-200"
                  }`}
                >
                  All Posts
                </button>
                {Object.entries(CATEGORY_INFO).map(([key, info]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedCategory(key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedCategory === key 
                        ? `bg-${info.color}-500 text-white` 
                        : "bg-white text-slate-600 border border-slate-200"
                    }`}
                  >
                    {info.emoji} {info.label}
                  </button>
                ))}
              </div>

              {/* Posts Grid */}
              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto mb-2" />
                  <p className="text-slate-500">Loading posts...</p>
                </div>
              ) : posts.length > 0 ? (
                <div className="grid gap-6 md:grid-cols-2">
                  {posts.map(post => {
                    const catInfo = CATEGORY_INFO[post.category] || CATEGORY_INFO.tips;
                    const postComments = comments[post.post_id] || [];
                    const isCommentsExpanded = expandedComments[post.post_id];
                    
                    return (
                      <div key={post.post_id} className="bg-white rounded-2xl shadow-md overflow-hidden border border-slate-100">
                        {/* Image */}
                        {post.image_url && (
                          <div className="w-full h-48 overflow-hidden">
                            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        
                        <div className="p-5">
                          {/* Category Badge */}
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-${catInfo.color}-100 text-${catInfo.color}-700 mb-3`}>
                            {catInfo.emoji} {catInfo.label}
                          </span>
                          
                          <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                            {post.title}
                          </h3>
                          <p className="text-slate-600 text-sm mb-4">{post.content}</p>
                          
                          {/* External Link */}
                          {post.external_link && (
                            <a
                              href={post.external_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 transition-colors mb-4"
                            >
                              <ExternalLink size={14} /> Learn More
                            </a>
                          )}
                          
                          {/* Prompt Question */}
                          {post.prompt_question && (
                            <div className="p-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-200 mb-4">
                              <p className="text-teal-800 font-medium text-sm italic">
                                💬 {post.prompt_question}
                              </p>
                            </div>
                          )}
                          
                          {/* Actions */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {post.comments_enabled && (
                                <button
                                  onClick={() => toggleComments(post.post_id)}
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium hover:bg-teal-200 transition-colors"
                                >
                                  <MessageCircle size={16} /> Comments
                                </button>
                              )}
                            </div>
                            <span className="text-xs text-slate-400">
                              {new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                            </span>
                          </div>
                          
                          {/* Comments Section */}
                          {isCommentsExpanded && (
                            <div className="mt-4 border-t border-slate-100 pt-4">
                              <div className="flex gap-2 mb-4">
                                <input
                                  type="text"
                                  value={newComment[post.post_id] || ""}
                                  onChange={(e) => setNewComment(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                                  placeholder="Share your experience..."
                                  className="flex-1 px-4 py-2 border border-teal-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
                                  onKeyPress={(e) => e.key === "Enter" && submitComment(post.post_id)}
                                />
                                <button
                                  onClick={() => submitComment(post.post_id)}
                                  disabled={submittingComment[post.post_id]}
                                  className="px-4 py-2 bg-teal-500 text-white rounded-full text-sm font-medium hover:bg-teal-600 transition-colors disabled:opacity-50"
                                >
                                  {submittingComment[post.post_id] ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                </button>
                              </div>
                              
                              {postComments.length > 0 ? (
                                <div className="space-y-3">
                                  {postComments.map((comment) => (
                                    <div key={comment.comment_id} className="bg-slate-50 rounded-xl p-3">
                                      <div className="flex items-center gap-2 mb-1">
                                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center text-white text-xs font-bold">
                                          {comment.author_name?.charAt(0).toUpperCase() || "?"}
                                        </div>
                                        <span className="text-sm font-medium text-slate-800">{comment.author_name}</span>
                                      </div>
                                      <p className="text-sm text-slate-600 pl-8">{comment.content}</p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-center text-slate-400 text-sm py-2">No comments yet. Share your thoughts!</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-2xl border border-slate-100">
                  <Calendar className="w-12 h-12 text-teal-300 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Posts Coming Soon!</h3>
                  <p className="text-slate-500 text-sm">Kiera is working on helpful content for SEN families.</p>
                  <p className="text-slate-500 text-sm">Check back every Friday at 2pm for new posts!</p>
                </div>
              )}
            </div>
          )}

          {/* Resources Section */}
          {activeSection === "resources" && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100">
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <BookOpen className="w-6 h-6 text-orange-500" />
                  Helpful Resources
                </h2>
                <p className="text-slate-600 mb-6">
                  Trusted organisations and resources to support your SEN journey.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {quickLinks.map((link, i) => (
                    <a
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors group"
                    >
                      <ExternalLink className="w-5 h-5 text-orange-500 mt-0.5 group-hover:text-orange-600" />
                      <div>
                        <h4 className="font-semibold text-slate-800 text-sm group-hover:text-orange-700">{link.title}</h4>
                        <p className="text-xs text-slate-600">{link.desc}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
              
              {/* Downloadable Guides */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
                <h3 className="font-bold text-amber-900 mb-4">📚 Free Guides (Coming Soon)</h3>
                <ul className="space-y-2 text-amber-800 text-sm">
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> EHCP Application Checklist</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Visual Schedule Templates</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> School Holiday Survival Guide</li>
                  <li className="flex items-center gap-2"><ChevronRight className="w-4 h-4" /> Sensory Diet Worksheet</li>
                </ul>
              </div>
            </div>
          )}

          {/* NHS & CWDT Section */}
          {activeSection === "nhs-cwdt" && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
                <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <Stethoscope className="w-6 h-6 text-blue-500" />
                  NHS & CWDT Guide
                </h2>
                <p className="text-slate-600 mb-6">
                  Understanding NHS services and Children with Disabilities Teams.
                </p>
                
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <h4 className="font-semibold text-blue-800 mb-2">What is CWDT?</h4>
                    <p className="text-sm text-blue-700">
                      Children with Disabilities Teams (CWDT) are specialist social work teams that support families with disabled children. They can help with respite care, direct payments, equipment, and more.
                    </p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-xl">
                    <h4 className="font-semibold text-blue-800 mb-2">Key NHS Services</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• CAMHS (Child and Adolescent Mental Health Services)</li>
                      <li>• Speech and Language Therapy (SALT)</li>
                      <li>• Occupational Therapy (OT)</li>
                      <li>• Physiotherapy</li>
                      <li>• Community Paediatrics</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 bg-indigo-50 rounded-xl">
                    <h4 className="font-semibold text-indigo-800 mb-2">Tips from Kiera</h4>
                    <ul className="text-sm text-indigo-700 space-y-1">
                      <li>💡 Keep a folder with all letters and assessments</li>
                      <li>💡 Take notes in every meeting (or ask to record)</li>
                      <li>💡 Follow up phone calls with an email for a paper trail</li>
                      <li>💡 You can request a social care assessment any time</li>
                      <li>💡 Join local parent carer forums for support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default SENFocus;
